import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays, ChevronDown, ChevronRight, FolderKanban, GitBranch, Home,
  Layers3, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Plus, Settings,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import useMediaQuery from "../hooks/useMediaQuery";
import { initials } from "../utils";
import GanttIcon from "./GanttIcon";
import NotificationCenter from "./NotificationCenter";
import WorkspaceIcon from "./WorkspaceIcon";
import "../styles/layout.css";

const EXPLORER_KEY = "gantt-pro.workspace-explorer";
const ROLE_LABEL = { owner: "Propietario", co_owner: "Copropietario", member: "Miembro", viewer: "Lector" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { activeOrganization, workspaces, contents, activeWorkspaceId, activeWorkspace, setActiveWorkspaceId } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = useMediaQuery("(max-width: 860px)");
  const [explorerOpen, setExplorerOpen] = useState(() => localStorage.getItem(EXPLORER_KEY) !== "0");
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => { if (!mobile) localStorage.setItem(EXPLORER_KEY, explorerOpen ? "1" : "0"); }, [explorerOpen, mobile]);
  useEffect(() => {
    if (activeWorkspaceId) setExpanded((current) => new Set([...current, activeWorkspaceId]));
  }, [activeWorkspaceId]);
  useEffect(() => { if (mobile) setExplorerOpen(false); }, [location.pathname, mobile]);

  const contentByWorkspace = useMemo(() => {
    const map = new Map();
    workspaces.forEach((workspace) => map.set(workspace.id, { projects: [], diagrams: [] }));
    contents.projects.forEach((item) => map.get(item.workspace_id)?.projects.push(item));
    contents.diagrams.forEach((item) => map.get(item.workspace_id)?.diagrams.push(item));
    return map;
  }, [contents, workspaces]);

  function selectWorkspace(id) {
    setActiveWorkspaceId(id);
    navigate("/");
  }

  function toggleWorkspace(id) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function openContent(workspaceId, path) {
    setActiveWorkspaceId(workspaceId);
    setExpanded((current) => new Set([...current, workspaceId]));
    navigate(path);
  }

  return (
    <div className={`app-shell ${explorerOpen ? "explorer-open" : ""}`}>
      <aside className="global-rail" aria-label="Navegación global">
        <button className="rail-brand" type="button" onClick={() => navigate("/")} data-tooltip="Plataforma de Gestión" aria-label="Plataforma de Gestión"><Layers3 size={20}/></button>
        <nav>
          <NavLink to="/" data-tooltip="Inicio" aria-label="Inicio" className={({ isActive }) => isActive ? "active" : ""}><Home size={19}/></NavLink>
          <NavLink to="/calendario" data-tooltip="Calendario" aria-label="Calendario" className={({ isActive }) => isActive ? "active" : ""}><CalendarDays size={19}/></NavLink>
          <button type="button" className={explorerOpen ? "active" : ""} onClick={() => setExplorerOpen((value) => !value)} data-tooltip="Explorador de espacios" aria-label="Explorador de espacios"><Menu size={19}/></button>
        </nav>
        <div className="rail-bottom">
          <NotificationCenter collapsed />
          <NavLink to="/configuracion/espacio" data-tooltip="Configuración" aria-label="Configuración" className={({ isActive }) => isActive ? "active" : ""}><Settings size={19}/></NavLink>
          <button type="button" onClick={logout} data-tooltip="Cerrar sesión" aria-label="Cerrar sesión"><LogOut size={18}/></button>
          <button type="button" className="rail-avatar" onClick={() => navigate("/configuracion/cuenta")} data-tooltip={`${user?.name || "Usuario"} · Mi cuenta`} aria-label={`${user?.name || "Usuario"} · Configurar cuenta`}>{initials(user?.name)}</button>
        </div>
      </aside>

      {mobile && explorerOpen && <button className="explorer-backdrop" aria-label="Cerrar explorador" onClick={() => setExplorerOpen(false)} />}
      <aside className={`workspace-explorer ${explorerOpen ? "open" : ""}`} aria-label="Explorador de espacios">
        <header className="explorer-header">
          <div><small>Organización</small><strong>{activeOrganization?.name || "Mi organización"}</strong></div>
          <button type="button" onClick={() => setExplorerOpen(false)} title="Ocultar explorador">{mobile ? <PanelLeftClose size={18}/> : <PanelLeftOpen size={18}/>}</button>
        </header>
        <div className="explorer-section-title"><span>Espacios de trabajo</span><button type="button" onClick={() => navigate("/configuracion/espacio?crear=1")} title="Crear espacio"><Plus size={15}/></button></div>
        <div className="workspace-tree">
          {workspaces.filter((workspace) => !workspace.is_archived).map((workspace) => {
            const open = expanded.has(workspace.id);
            const group = contentByWorkspace.get(workspace.id) || { projects: [], diagrams: [] };
            return <div className={`workspace-tree-node ${workspace.id === activeWorkspaceId ? "selected" : ""}`} key={workspace.id}>
              <div className="workspace-tree-row">
                <button className="tree-chevron" type="button" onClick={() => toggleWorkspace(workspace.id)} aria-label={open ? "Contraer" : "Expandir"}>{open ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}</button>
                <button className="tree-workspace" type="button" onClick={() => selectWorkspace(workspace.id)}><WorkspaceIcon iconKey={workspace.icon_key} size={17}/><span>{workspace.name}</span></button>
              </div>
              {open && <div className="tree-children">
                <button type="button" className={location.pathname === "/" && workspace.id === activeWorkspaceId ? "active" : ""} onClick={() => selectWorkspace(workspace.id)}><FolderKanban size={15}/><span>Resumen</span></button>
                {group.projects.map((project) => <button type="button" className={location.pathname === `/gantt/${project.public_id}` || location.pathname === `/projects/${project.public_id}` ? "active" : ""} key={project.public_id} onClick={() => openContent(workspace.id, `/gantt/${project.public_id}`)}><GanttIcon size={14}/><span>{project.name}</span></button>)}
                {group.diagrams.map((diagram) => <button type="button" className={location.pathname === `/diagramas/${diagram.public_id}` ? "active" : ""} key={diagram.public_id} onClick={() => openContent(workspace.id, `/diagramas/${diagram.public_id}`)}><GitBranch size={14}/><span>{diagram.name}</span></button>)}
                {!group.projects.length && !group.diagrams.length && <span className="tree-empty">Sin contenido</span>}
              </div>}
            </div>;
          })}
        </div>
        <footer className="explorer-footer"><WorkspaceIcon iconKey={activeWorkspace?.icon_key}/><div><strong>{activeWorkspace?.name || "Espacio"}</strong><small>{ROLE_LABEL[activeWorkspace?.role] || "Miembro"}</small></div><button type="button" onClick={() => navigate("/configuracion/espacio")} aria-label="Administrar espacio"><Settings size={16}/></button></footer>
      </aside>

      <main className="main">{children}</main>
    </div>
  );
}
